/**
 * Schema Builder domain types.
 *
 * Models the landing page schema cards, the schema edit page, editable columns,
 * schema history, mapped files, rule presets, and configured schema rules.
 *
 * Kept separate from `types/index.ts` so the feature can evolve without churn
 * in the core upload, mapping, and data quality types.
 */

export type SchemaStatus = "draft" | "published" | "preset" | "archived";

export type SchemaStatusFilterValue = "all" | "active_for_provider" | SchemaStatus;

export type SchemaDataType = "General" | "Integer" | "Decimal" | "Date & Time" | "Boolean";

// Single source of truth for the Data Type selector — derive options from this instead
// of hardcoding per call site.
export const SCHEMA_DATA_TYPES: SchemaDataType[] = [
  "General",
  "Boolean",
  "Decimal",
  "Integer",
  "Date & Time",
];

export type SchemaRuleCategory = "All" | "Arithmetic" | "If-Then" | "Chronological" | "Syntactical";

// Single source of truth for rule-category filter tabs — derive options from this instead
// of hardcoding per call site.
export const SCHEMA_RULE_CATEGORIES: SchemaRuleCategory[] = [
  "All",
  "Arithmetic",
  "Syntactical",
  "If-Then",
  "Chronological",
];

export type DataQualityRuleType =
  | "Data Type"
  | "Mandatory"
  | "Summation"
  | "Conditional"
  | "Duplicates"
  | "Pattern"
  | "Reference List";

export interface SchemaRulePreset {
  id: string;
  backendRuleType: string;
  dqType?: DataQualityRuleType;
  title: string;
  category: SchemaRuleCategory;
  description: string;
  applicableDataTypes: SchemaDataType[];
  expression?: string;
  defaultCriticality?: "error" | "warn" | null;
  parametersSchema?: Record<string, unknown>;
  scope?: string;
}

export interface SchemaTemplate {
  id: string;
  name: string;
  description: string;
  status: Extract<SchemaStatus, "preset" | "published">;
  totalColumns: number;
  rules: number;
  crossColumnRules?: number;
  knownMappings: number;
}

export interface SchemaSummary {
  id: string;
  name: string;
  description: string;
  status: SchemaStatus;
  totalColumns: number;
  rules: number;
  dependencies?: number;
  conflicts?: number;
  filesRemapped?: number;
  lastEditor?: string;
  lastEditedAt?: string;
  lastUsedAt?: string;
  lastUpdatedAt?: string;
  publishedAt?: string;
  createdAt: string;
  providerId?: string | null;
}

export interface SchemaMappedFile {
  fileName: string;
  mappedAt: string;
  uploadedBy?: string;
}

export interface SchemaColumn {
  fieldId: string;
  id: string;
  sortOrder: number;
  name: string;
  example: string;
  description: string;
  mandatory: boolean;
  unique: boolean;
  dataType: SchemaDataType;
  formatting: string;
}

export interface SchemaDetail extends SchemaSummary {
  columns: SchemaColumn[];
  mappedFiles: SchemaMappedFile[];
}

export type SchemaActivityEventType =
  | "schema_created"
  | "schema_renamed"
  | "schema_published"
  | "schema_archived"
  | "column_added"
  | "column_edited"
  | "column_deleted"
  | "rule_added"
  | "rule_deleted"
  | "reference_list_added"
  | "reference_list_updated"
  | "reference_list_deleted"
  | "draft_saved"
  | "file_uploaded";

export interface SchemaActivityEvent {
  id: string;
  type: SchemaActivityEventType;
  actor: string;
  timestamp: string;
  detail?: string;
}

export interface SchemaRule {
  id: string;
  title: string;
  category: SchemaRuleCategory;
  fieldCount: number;
  ruleKey: string;
  columnIds: string[];
  formula: string;
  description: string;
  hasConflict: boolean;
  conflictWith?: string[];
  conflictReason?: string;
  criticality?: "error" | "warn" | null;
  backendRuleType?: string;
  dqType?: DataQualityRuleType;
  presetId?: string;
  parameters?: Record<string, unknown>; // raw backend parameters, preserved for round-trip
  scope?: string;
}

// "deferred_full_run" covers cross-row checks (e.g. uniqueness) that can't be judged on a
// sample; "skipped" means the rule's column wasn't found in the uploaded file.
export type ValidationRuleStatus = "evaluated" | "deferred_full_run" | "skipped";

export interface ValidationRuleResult {
  ruleId: string;
  columnName: string;
  isMultiColumn: boolean;
  ruleType: string;
  status: ValidationRuleStatus;
  passed: boolean;
  summary: string;
  failingSample?: Array<{
    sampleIndex: number;
    values: Array<{ column: string; value: string }>;
  }>;
}

export interface ValidationResult {
  schemaName: string;
  fileName: string;
  rowsTested: number;
  rulesTested: number;
  passed: number;
  failed: number;
  skipped: number;
  ruleResults: ValidationRuleResult[];
  unmappedColumns: string[];
}
